import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, 
  Clock, 
  X, 
  Target,
  TrendingUp
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ComprehensionAssessmentModal } from './comprehension-assessment-modal';
import { useAuth } from '@/components/auth/AuthProvider';
import { comprehensionService } from '@/services/comprehension-service';

interface ComprehensionAssessmentBannerProps {
  onDismiss?: () => void;
  isReassessment?: boolean;
  daysOverdue?: number;
}

export function ComprehensionAssessmentBanner({ 
  onDismiss, 
  isReassessment = false,
  daysOverdue = 0 
}: ComprehensionAssessmentBannerProps) {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);

  // Check for active session
  useEffect(() => {
    if (user?.id) {
      const activeSession = comprehensionService.hasActiveSession(user.id);
      setHasActiveSession(activeSession);
    }
  }, [user?.id]);

  const handleTakeAssessment = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    // Recheck session state after modal closes
    if (user?.id) {
      const activeSession = comprehensionService.hasActiveSession(user.id);
      setHasActiveSession(activeSession);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) {
    return null;
  }

  const isOverdue = daysOverdue > 0;

  return (
    <>
      <Alert className={`border-l-4 ${
        isOverdue 
          ? 'border-l-red-500 bg-red-50 border-red-200' 
          : isReassessment 
            ? 'border-l-orange-500 bg-orange-50 border-orange-200' 
            : 'border-l-blue-500 bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-start justify-between w-full">
          <div className="flex items-start space-x-3 flex-1">
            <div className={`p-2 rounded-lg ${
              isOverdue 
                ? 'bg-red-100' 
                : isReassessment 
                  ? 'bg-orange-100' 
                  : 'bg-blue-100'
            }`}>
              <Brain className={`h-5 w-5 ${
                isOverdue 
                  ? 'text-red-600' 
                  : isReassessment 
                    ? 'text-orange-600' 
                    : 'text-blue-600'
              }`} />
            </div>
            
            <div className="flex-1 space-y-2">
              <div className="flex items-center space-x-2">
                <h4 className={`font-semibold ${
                  isOverdue 
                    ? 'text-red-800' 
                    : isReassessment 
                      ? 'text-orange-800' 
                      : 'text-blue-800'
                }`}>
                  {isReassessment 
                    ? 'Comprehension Re-assessment Available' 
                    : 'Comprehension Assessment Available'
                  }
                </h4>
                
                {isOverdue && (
                  <Badge variant="destructive" className="text-xs">
                    {daysOverdue} days overdue
                  </Badge>
                )}
                
                {isReassessment && !isOverdue && (
                  <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">
                    6-month update
                  </Badge>
                )}
              </div>
              
              <AlertDescription className={`${
                isOverdue 
                  ? 'text-red-700' 
                  : isReassessment 
                    ? 'text-orange-700' 
                    : 'text-blue-700'
              } max-w-2xl`}>
                {isReassessment 
                  ? "It's time to update your comprehension profile! Taking this assessment helps us continue providing explanations at your current learning level."
                  : "Help us personalize your learning experience by taking a quick comprehension assessment. This will allow us to explain concepts at your understanding level."
                }
              </AlertDescription>

              {/* Benefits */}
              <div className="flex flex-wrap gap-4 text-xs pt-1">
                <div className="flex items-center space-x-1">
                  <Target className="h-3 w-3" />
                  <span>Personalized explanations</span>
                </div>
                <div className="flex items-center space-x-1">
                  <TrendingUp className="h-3 w-3" />
                  <span>Better learning outcomes</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>~5 minutes</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            <Button 
              onClick={handleTakeAssessment}
              size="sm"
              className={
                isOverdue 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : isReassessment 
                    ? 'bg-orange-600 hover:bg-orange-700' 
                    : ''
              }
            >
              {isReassessment 
                ? 'Update Profile' 
                : hasActiveSession 
                  ? 'Resume Assessment' 
                  : 'Take Assessment'
              }
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleDismiss}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Alert>

      <ComprehensionAssessmentModal 
        open={showModal} 
        onOpenChange={handleModalClose} 
      />
    </>
  );
}
